"use client";

import { useState } from "react";
import {
  DataTable,
  PillGroup,
  SelectField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H3 } from "@/components/tool-kit/styles";
import {
  CriticalPanel,
  RefractionPanel,
  UnknownPanel,
} from "@/components/tools/physics-waves/snell-panels";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import {
  MATERIAL_INDEX,
  MATERIAL_ORDER,
  speedInMedium,
} from "@/lib/tool-kit/physics-waves/refraction";

/** English strings. A translated content file overrides these through `ui`. */
export const SNELLS_LAW_CALCULATOR_STRINGS = {
  modeLabel: "What do you want to do?",
  modes: {
    refraction: "Find the refracted ray",
    unknown: "Solve for any term",
    critical: "Critical angle",
  },
  sigFigs: "Round to this many significant figures",
  results: "Results",
  enterAll: "Enter a valid value in every box to see the result.",
  medium1: "Medium 1 (the light starts here)",
  medium2: "Medium 2 (the light enters here)",
  materials: {
    vacuum: "Vacuum",
    air: "Air (0 °C, 1 atm)",
    ice: "Ice",
    water: "Water (20 °C)",
    ethanol: "Ethanol",
    vegetableOil: "Vegetable oil",
    glycerol: "Glycerol",
    fusedSilica: "Fused silica",
    acrylic: "Acrylic (PMMA)",
    benzene: "Benzene",
    crownGlass: "Crown glass (N-BK7)",
    salt: "Rock salt (NaCl)",
    carbonDisulfide: "Carbon disulfide",
    sapphire: "Sapphire",
    flintGlass: "Dense flint glass (N-SF10)",
    diamond: "Diamond",
  },
  customOption: "Enter my own index…",
  customLabel: "Refractive index",
  optionLabel: "{name} (n = {n})",
  incidenceInput: "Angle of incidence θ₁ (degrees from the normal)",
  refractionInput: "Angle of refraction θ₂ (degrees from the normal)",
  theta2: "Angle of refraction (θ₂)",
  totalInternal: "Total internal reflection",
  deviation: "Bend from the straight path",
  critical: "Critical angle",
  noCritical: "None going into a denser medium",
  brewster: "Brewster's angle",
  reflected: "Light reflected (unpolarised)",
  transmitted: "Light transmitted",
  speed1: "Speed of light in medium 1",
  speed2: "Speed of light in medium 2",
  working: "The working",
  stepTheta2:
    "n₁ sin θ₁ = n₂ sin θ₂, so sin θ₂ = {n1} × sin({theta1}) ÷ {n2} = {sine} and θ₂ = {theta2}",
  stepTir:
    "sin θ₂ would be {sine}, which is greater than 1, so there is no refracted ray and all the light is reflected. The critical angle is {critical}.",
  errors: {
    "invalid-index": "A refractive index must be a number of at least 1.",
    "invalid-angle":
      "Enter an angle from 0 up to, but not including, 90 degrees.",
    "no-solution":
      "No solution: the sine of an angle would have to exceed 1, which means total internal reflection or an impossible combination.",
  },
  mediumTop: "Medium 1: n₁ = {n}, θ₁ = {angle}",
  mediumBottom: "Medium 2: n₂ = {n}, θ₂ = {angle}",
  mediumBottomNone: "Medium 2: n₂ = {n} (no refracted ray)",
  figureLabel: "Ray diagram for refraction at a boundary",
  figureDescription:
    "An incident ray striking a boundary between two media, with the refracted ray below and a faint reflected ray above, all measured from the dashed normal.",
  targetLabel: "What do you want to find?",
  targets: {
    theta2: "Angle of refraction (θ₂)",
    n2: "Index of medium 2 (n₂)",
    theta1: "Angle of incidence (θ₁)",
    n1: "Index of medium 1 (n₁)",
  },
  closest: "Closest listed material",
  closestValue: "{name} (n = {n})",
  stepSolve:
    "Rearranging n₁ sin θ₁ = n₂ sin θ₂ for the unknown gives {target} = {result}",
  stepCritical: "sin θc = n₂ ÷ n₁ = {n2} ÷ {n1} = {ratio}, so θc = {critical}",
  criticalNote:
    "Light travelling in medium 1 that meets the surface at more than this angle is totally reflected — the principle behind optical fibres, the sparkle of diamond and prism binoculars.",
  noCriticalNote:
    "Light entering a denser medium, or one of equal index, always refracts. Total internal reflection needs n₁ to be greater than n₂.",
  tableHeading: "Refractive indices of common materials",
  columnMaterial: "Material",
  columnIndex: "Index n",
  columnSpeed: "Speed of light (10⁸ m/s)",
  tableNote:
    "Sodium D line (589 nm) values from Hecht, Optics; gases at 0 °C and 1 atm, liquids at 20 °C. The two optical glasses are Schott catalogue values at 587.6 nm. Real indices vary with wavelength and temperature.",
} as const;

type Mode = keyof typeof SNELLS_LAW_CALCULATOR_STRINGS.modes;

const SIG_CHOICES = ["3", "4", "5"] as const;

export default function SnellsLawCalculatorWidget() {
  const strings = useToolUi(SNELLS_LAW_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("refraction");
  const [sigText, setSigText] = useState<(typeof SIG_CHOICES)[number]>("4");
  const sig = Number(sigText);
  const rows = MATERIAL_ORDER.map((id) => [
    strings.materials[id],
    fmt(MATERIAL_INDEX[id], {
      maximumFractionDigits: 6,
      minimumFractionDigits: 2,
    }),
    fmt(speedInMedium(MATERIAL_INDEX[id]) / 1e8, { maximumFractionDigits: 3 }),
  ]);
  return (
    <>
      <PillGroup
        label={strings.modeLabel}
        name="snell-mode"
        onChange={setMode}
        options={(Object.keys(strings.modes) as Mode[]).map((value) => ({
          value,
          label: strings.modes[value],
        }))}
        value={mode}
      />
      <div className="max-w-xs">
        <SelectField
          id="snell-sig"
          label={strings.sigFigs}
          onChange={setSigText}
          options={SIG_CHOICES.map((value) => ({ value, label: value }))}
          value={sigText}
        />
      </div>
      {mode === "refraction" ? (
        <RefractionPanel fmt={fmt} sig={sig} strings={strings} />
      ) : null}
      {mode === "unknown" ? (
        <UnknownPanel fmt={fmt} sig={sig} strings={strings} />
      ) : null}
      {mode === "critical" ? (
        <CriticalPanel fmt={fmt} sig={sig} strings={strings} />
      ) : null}
      <div className="space-y-3">
        <h2 className={WIDGET_H3}>{strings.tableHeading}</h2>
        <DataTable
          head={[
            strings.columnMaterial,
            strings.columnIndex,
            strings.columnSpeed,
          ]}
          rows={rows}
        />
        <p className="text-muted-foreground text-xs">{strings.tableNote}</p>
      </div>
    </>
  );
}
